// The access token expiry can arrive as a Unix epoch in seconds or in milliseconds. A value
// below this threshold (1e12) is treated as seconds and scaled to milliseconds before use.
export const MIN_SECONDS_VALUE = 1000000000000;

// The app proactively refreshes the access token once it is within this many minutes of expiry.
export const MINUTES_THRESHOLD = 30;

export interface AuthState {
    login: LoginState;
    session: Session;
    lastActivity: Date | string;
}

export interface LoginState {
    isFetching: boolean;
    message: string;
    secondaryMessage?: string;
    nonPilotUserLoggedIn: boolean;
    artaLoginUrl?: string;
    isFirstStage2fa: boolean;
    isVerifyingOtp: boolean;
    isResendingOtp: boolean;
    // Set when profile validation fails after a successful OTP; the 2FA form then shows a terminal
    // error screen (using `message`/`secondaryMessage`) instead of the OTP input.
    otpProfileError?: boolean;
    maskedEmail?: string;
    otpResendMessage?: string;
    username?: string;
}

// Why a session ended. The server decides this and puts it on /login as the `reason`
// query param. Kept in sync with KnownSessionEndReasons in Web/Auth/SessionEndReason.cs.
export enum KnownSessionEndReasons {
    PasswordChanged = "passwordChanged",
    SessionEnded = "sessionEnded",
    // Two-factor stopped applying between the two stages of a sign in - the only way a pending
    // second stage ends that the member cannot recover from on the code form.
    TwoFactorDisabled = "twoFactorDisabled"
}

// What the log in page tells a member about why they are back on it. Rendered separately
// from LoginState.message, which is always an error.
export interface SessionEndMessage {
    text: string;
    isError: boolean;
}

const SESSION_END_MESSAGES: Record<KnownSessionEndReasons, SessionEndMessage> = {
    [KnownSessionEndReasons.PasswordChanged]: {
        text: "Your password has been changed. Please sign in again.",
        isError: false
    },
    [KnownSessionEndReasons.SessionEnded]: {
        text: "Your session has ended. Please sign in again.",
        isError: false
    },
    // Vague on purpose. The member did nothing wrong and there is nothing for them to do
    // differently - two-factor was switched off underneath them, and signing in again just works.
    [KnownSessionEndReasons.TwoFactorDisabled]: {
        text: "Something went wrong during the login process. Please try again.",
        isError: true
    }
};

/**
 * Maps a `reason` query param to its message. The param is whatever is in the address
 * bar, so match it against the known reasons rather than indexing the map directly -
 * otherwise `?reason=__proto__` resolves to an inherited property.
 */
export const sessionEndMessageFor = (reason: string | null): SessionEndMessage | null => {
    const knownReason = Object.values(KnownSessionEndReasons).find((known) => known === reason);
    return knownReason ? SESSION_END_MESSAGES[knownReason] : null;
};

// Body of a 401 from /api/authentication/refresh. `sessionMissing` distinguishes "there
// was no session to refresh" from "the refresh token itself was rejected".
export interface RefreshFailure {
    sessionMissing?: boolean;
}

// Body of a 401 from /api/authentication/otp/login. `sessionEnded` marks the one case the member
// cannot recover from - 2FA stopped applying between the stages, which the server has already
// signed them out of. Everything else, including a lapsed session, carries no body, so the absence
// of this flag is what keeps a typo or a slow member from being ejected.
export interface VerifyOtpFailure {
    sessionEnded?: boolean;
}

// Why a verify attempt failed. The 401 carries no reason, so a wrong code and an expired one are
// one case here - `error` is everything that says nothing about the code itself.
export type OtpFailureReason = "invalidOrExpired" | "error";

export interface Session {
    userId: string | null;
    accessTokenExpiry: number;
    inactiveReauthRequiredMs: number;
    inactiveForcedLogoutMs: number;
    idle: boolean;
    antiforgeryToken: string;
    emulatedSession: boolean;
    isFirstStage2fa?: boolean;
}

export interface AuthResponse {
    userId?: string;
    accessTokenExpiry?: number;
    antiforgeryToken?: string;
    succeeded: boolean;
    emulatedSession?: boolean;

    message?: string;
    secondaryMessage?: string;
    isVerificationExpired?: boolean;
    emailSentDateTime?: Date | string;
    emailAddress?: string;
    artaLoginUrl?: string;
    isFirstStage2fa?: boolean;
    maskedEmail?: string;
}
